/**
 * PrepHistory Component
 *
 * Displays recipe preparation history with dates, notes, and servings.
 * Features:
 * - "Log Preparation" button opens LogPrepModal
 * - Delete button on each prep with confirmation
 * - Refresh after add/delete
 */

import { useState, useCallback } from 'react';
import type { RecipePrep } from '@/types/food';
import { LogPrepModal } from './LogPrepModal';

interface PrepHistoryProps {
  prepHistory: RecipePrep[];
  /** Recipe ID for API calls */
  recipeId: number;
  /** Default servings from recipe */
  defaultServings?: number | null;
  /** Callback when preps change (for page refresh) */
  onPrepsChange?: () => void;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function PlusIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function TrashIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function PrepHistory({
  prepHistory,
  recipeId,
  defaultServings,
  onPrepsChange,
}: PrepHistoryProps) {
  const [showLogModal, setShowLogModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleLogSave = useCallback(() => {
    onPrepsChange?.();
  }, [onPrepsChange]);

  const handleDelete = useCallback(
    async (prepId: number) => {
      if (!confirm('Are you sure you want to delete this preparation record?')) {
        return;
      }

      setDeletingId(prepId);
      setDeleteError(null);

      try {
        const response = await fetch(`/food/recipes/${recipeId}/preps/${prepId}`, {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
          },
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to delete preparation');
        }

        onPrepsChange?.();
      } catch (err) {
        setDeleteError(err instanceof Error ? err.message : 'Failed to delete');
      } finally {
        setDeletingId(null);
      }
    },
    [recipeId, onPrepsChange]
  );

  return (
    <div>
      {deleteError && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {deleteError}
        </div>
      )}

      {prepHistory.length > 0 ? (
        <div className="flex flex-col gap-2">
          {prepHistory.map((prep) => (
            <div
              key={prep.id}
              className="flex justify-between items-center px-3 py-2.5 bg-gray-50 rounded-lg text-sm group"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900">
                  {formatDate(prep.preparedAt)}
                </div>
                {prep.notes && (
                  <div className="text-gray-500 text-xs mt-0.5 truncate">{prep.notes}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {prep.servingsMade && (
                  <div className="text-gray-500 text-xs">
                    {prep.servingsMade} servings
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(prep.id)}
                  disabled={deletingId === prep.id}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                  aria-label="Delete preparation"
                  data-testid={`delete-prep-${prep.id}`}
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-sm">No preparation history yet.</p>
      )}

      <button
        type="button"
        onClick={() => setShowLogModal(true)}
        className="w-full mt-3 px-3 py-3 bg-green-50 border-2 border-dashed border-green-300 rounded-lg text-green-700 font-medium text-sm hover:bg-green-100 hover:border-green-400 transition-colors flex items-center justify-center gap-2"
        data-testid="log-prep-button"
      >
        <PlusIcon />
        Log Preparation
      </button>

      {/* Log Prep Modal */}
      <LogPrepModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        onSave={handleLogSave}
        recipeId={recipeId}
        defaultServings={defaultServings}
      />
    </div>
  );
}
